// Box-select model for "box orders" — forestry tools and the terraform orders
// (dig / fill / fillsand). Shared by the 3D and 2.5D input paths so both behave
// identically. Unlike a road's press-and-drag PATH stroke, a box order is laid by
// rubber-banding a rectangle over an area: every matching hex inside it is selected.
// The selection lives in st.paintPath as a HIGHLIGHT only (the same field the overlays
// already render) — nothing reaches the server until release.
//
// Two sub-modes (st.chopCancel — shared by all box orders):
//   false → QUEUE: box picks unqueued eligible hexes; commit sends a 'build' order each.
//   true  → REMOVE: box picks the player's existing orders of that type; commit demolishes.
//
// Each caller supplies an `inBox(q,r)` predicate in its own projection (3D projects the
// hex through the camera; 2.5D tests the hex's world position against the drag AABB), so
// the geometry stays renderer-specific while the selection/commit logic is shared.

import type { GameState } from './state'
import type { Building, BuildingType } from '../shared/types'
import type { ClientMsg } from '../shared/protocol'
import { CHUNK } from '../shared/hex'
import { BUILDINGS } from '../shared/constants'
import { ghostValid } from './render/renderer'

/** Safety cap on hexes selected by one box — keeps a huge drag from bursting thousands
 *  of orders at the server (and the preview from stalling). Silently truncates. */
export const BOX_ORDER_MAX = 400

/** The player's order of `type` standing on hex (q,r), or null. */
export function orderAt(st: GameState, type: BuildingType, q: number, r: number): Building | null {
  for (const b of st.buildings.values()) {
    if (b.type === type && b.owner === st.me && b.q === q && b.r === r) return b
  }
  return null
}

/** Compute the highlighted selection for a box order. In QUEUE mode: every eligible,
 *  unqueued hex whose `inBox` is true (ok = passes the placement rule). In REMOVE mode:
 *  every existing own order of `type` whose `inBox` is true (ok = true). Capped at BOX_ORDER_MAX. */
export function boxOrderCells(
  st: GameState,
  type: BuildingType,
  inBox: (q: number, r: number) => boolean,
  cancel: boolean,
): { q: number; r: number; ok: boolean }[] {
  const out: { q: number; r: number; ok: boolean }[] = []
  if (cancel) {
    for (const b of st.buildings.values()) {
      if (b.type !== type || b.owner !== st.me) continue
      if (inBox(b.q, b.r)) out.push({ q: b.q, r: b.r, ok: true })
      if (out.length >= BOX_ORDER_MAX) break
    }
    return out
  }
  // QUEUE mode: skip hexes that already carry an order of this type (they'd re-queue as
  // invalid). Only test hexes the order can even target — a chop wants a standing tree,
  // a terraform wants its `on` terrain — so a big box doesn't ghostValid-check the whole
  // map.
  const on = BUILDINGS[type].on
  const treeGated = type === 'chop'
  const queued = new Set<string>()
  for (const b of st.buildings.values()) {
    if (b.type === type && b.owner === st.me) queued.add(b.q + ',' + b.r)
  }
  // Cap on VALID (placeable) cells, not total — so a huge box whose leading tiles all
  // fail the rule (e.g. a coastal plain below the dig waterline) doesn't burn the cap
  // before reaching the diggable hexes elsewhere. A looser total cap bounds the preview.
  let valid = 0
  for (const c of st.chunks.values()) {
    for (let i = 0; i < c.tiles.length; i++) {
      if (treeGated ? c.trees?.[i] !== 1 : on && !on.includes(c.tiles[i])) continue
      if (type === 'plant' && c.trees?.[i] === 1) continue
      const q = c.cx * CHUNK + (i % CHUNK)
      const r = c.cy * CHUNK + Math.floor(i / CHUNK)
      if (queued.has(q + ',' + r)) continue
      if (!inBox(q, r)) continue
      const ok = ghostValid(st, type, q, r)
      out.push({ q, r, ok })
      if (ok) valid++
      if (valid >= BOX_ORDER_MAX || out.length >= 3 * BOX_ORDER_MAX) return out
    }
  }
  return out
}

/** Release the box: QUEUE mode sends a 'build' for every still-valid selected hex;
 *  REMOVE mode 'demolish'es the order on every selected hex. Clears the highlight.
 *  Returns how many orders were sent. */
export function boxOrderCommit(st: GameState, send: (m: ClientMsg) => void, type: BuildingType, cancel: boolean): number {
  let sent = 0
  for (const c of st.paintPath) {
    if (cancel) {
      const b = orderAt(st, type, c.q, c.r)
      if (b) { send({ t: 'demolish', id: b.id }); sent++ }
    } else if (ghostValid(st, type, c.q, c.r)) {
      send({ t: 'build', type, q: c.q, r: c.r }); sent++
    }
  }
  st.paintPath = []
  st.paintType = null
  return sent
}

/** Act on a single hex (a plain click, no drag) with a box-order tool: QUEUE places one
 *  order, REMOVE cancels the order under the cursor. Returns true if anything was sent. */
export function orderClick(st: GameState, send: (m: ClientMsg) => void, type: BuildingType, q: number, r: number): boolean {
  if (st.chopCancel) {
    const b = orderAt(st, type, q, r)
    if (b) { send({ t: 'demolish', id: b.id }); return true }
    return false
  }
  if (ghostValid(st, type, q, r)) { send({ t: 'build', type, q, r }); return true }
  return false
}
